import {
  Checkbox,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"

import {
  INVENTORY_LOT_COLUMN_COUNT,
  INVENTORY_PALLET_COLUMN_COUNT,
  InventoryLotTableHeaderCells,
  InventoryLotTableRowCells,
  InventoryPalletTableHeaderCells,
  InventoryPalletTableRowCells,
} from "../../../../components"
import { isPalletLocked } from "../../utils"

import { AerosMultiSelect } from "@/components/UiKit/AerosMultiSelect"
import type { InventoryPalletGroup, InventoryPalletGroupsSortField, SortingOrder } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

const PALLET_SELECTION_COLUMN_COUNT = 1

const COLUMN_COUNT = PALLET_SELECTION_COLUMN_COUNT + INVENTORY_PALLET_COLUMN_COUNT + INVENTORY_LOT_COLUMN_COUNT

interface TransferPalletSelectionProps {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  selectedReferenceNbrs: Set<number>
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  aisleCodes: string[]
  aisleOptions: Option<string>[]
  uomName: string
  onPalletToggle: (pallet: InventoryPalletGroup) => void
  onSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  onSearchChange: (search: string) => void
  onAisleCodesChange: (aisleCodes: string[]) => void
}

export const TransferPalletSelection: FC<TransferPalletSelectionProps> = ({
  palletGroups,
  isFetching,
  selectedReferenceNbrs,
  sortBy,
  sortDirection,
  aisleCodes,
  aisleOptions,
  uomName,
  onPalletToggle,
  onSortChange,
  onSearchChange,
  onAisleCodesChange,
}) => {
  const [search, setSearch] = useState("")

  return (
    <div className='flex min-h-0 flex-1 flex-col rounded-lg border border-gray-400 p-4'>
      <div className='mb-4 flex items-center justify-between gap-4'>
        <Typography variant='h6' className='flex-1 font-bold leading-10 text-gray-800'>
          1. Select Pallets
        </Typography>
        <div className='flex items-end justify-end gap-4'>
          <TextField
            placeholder='Search by Reference #, Lot or Batch'
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              onSearchChange(e.target.value)
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='end'>
                    <SearchOutlined fontSize='small' />
                  </InputAdornment>
                ),
              },
            }}
            className='w-[360px]'
            data-testid='transfer.pallet-selection.search-field'
          />
          <AerosMultiSelect
            label='Filter by Aisle'
            placeholder='Select one'
            options={aisleOptions}
            value={aisleCodes}
            onChange={onAisleCodesChange}
            withSelectAll
            className='w-[360px]'
            data-testid='transfer.pallet-selection.aisle-field'
          />
        </div>
      </div>
      <div className='relative flex min-h-0 flex-1'>
        {isFetching && (
          <div className='absolute inset-0 top-[53px] z-10 flex items-center justify-center bg-white/60'>
            <CircularProgress size={48} />
          </div>
        )}
        <TableContainer className='overflow-auto'>
          <Table stickyHeader>
            <TableHead className='bg-gray-100'>
              <TableRow>
                <TableCell className='w-12' />
                <InventoryPalletTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
                <InventoryLotTableHeaderCells sort={{ sortBy, sortDirection, onSortChange }} />
              </TableRow>
            </TableHead>

            <TableBody>
              {!palletGroups.length && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align='center' className='py-8'>
                    No pallets found
                  </TableCell>
                </TableRow>
              )}

              {palletGroups.map((pallet) => {
                const isSelectionDisabled = isPalletLocked(pallet)
                const isSelected = selectedReferenceNbrs.has(pallet.referenceNbr)

                return pallet.lots.map((lot, lotIndex) => {
                  const isFirstLot = lotIndex === 0

                  return (
                    <TableRow
                      key={lot.rawInventoryId}
                      className={isSelectionDisabled ? "bg-gray-100" : undefined}
                      data-testid={`transfer.pallet-selection.row-${lot.rawInventoryId}`}
                    >
                      {isFirstLot ? (
                        <Tooltip
                          title='This pallet is locked due to a pending adjustment, work order, lot selection, or open goods receipt.'
                          disableHoverListener={!isSelectionDisabled}
                        >
                          <TableCell>
                            <Checkbox
                              size='small'
                              checked={isSelected}
                              disabled={isSelectionDisabled}
                              onChange={() => onPalletToggle(pallet)}
                              data-testid={`transfer.pallet-selection.pallet-checkbox-${pallet.referenceNbr}`}
                            />
                          </TableCell>
                        </Tooltip>
                      ) : (
                        <TableCell className='h-[60px]' />
                      )}
                      <InventoryPalletTableRowCells pallet={pallet} isFirstLot={isFirstLot} uomName={uomName} />
                      <InventoryLotTableRowCells lot={lot} uomName={uomName} />
                    </TableRow>
                  )
                })
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}
